import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useLazyQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Search,
  Monitor,
  Image,
  ListVideo,
  Calendar,
  CalendarClock,
  Folder,
  Rss,
  Smartphone,
  Users2,
  User,
  X,
  Loader2,
} from 'lucide-react';
import { GLOBAL_SEARCH, GLOBAL_SEARCH_ASSETS_ENGAGES } from '@scio3/graphql-client';
import { ROOT_TEAM_ID } from '@scio3/shared-types';
import { isEnterKey } from '@/lib/keyboard';
import { usePermissions } from '@/hooks/usePermissions';
import { useTeamStore } from '@/stores/team-store';
import { useScreenFilterStore } from '@/features/screens/stores/screen-filter-store';
import { useAssetFilterStore } from '@/features/assets/stores/asset-filter-store';
import { usePlaylistFilterStore } from '@/features/playlists/stores/playlist-filter-store';
import { useScheduleSidebarStore } from '@/features/schedules/stores/schedule-sidebar-store';
import { useEngageFilterStore } from '@/features/engage/stores/engage-filter-store';
import { usePlaylistSelectionStore } from '@/features/playlists/stores/playlist-selection-store';
import { useScheduleSelectionStore } from '@/features/schedules/stores/schedule-selection-store';
import {
  Command,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@scio3/ui';

// Progressive-rendering split. Two Apollo queries hit the same backend
// `globalSearch` resolver with different `include` allow-lists — the wire
// response shape stays `{ globalSearch: {...} }` in both, just with different
// fields populated. Fast paints at ~150ms, slow (assets/engages) fills in
// behind a section-level spinner ~700-1500ms later depending on env.
interface GlobalSearchResult {
  globalSearch: {
    screens: Array<{
      _id: string;
      deviceName?: string;
      name?: string;
      status?: string;
      os?: string;
    }>;
    playlists: Array<{ _id: string; name: string; tags?: string[] }>;
    schedules: Array<{ _id: string; name: string; type?: string; tags?: string[] }>;
    folders: Array<{ _id: string; name: string; type?: string; path?: string }>;
    feeds: Array<{
      _id: string;
      name: string;
      tags?: string[];
      thumbnail?: string;
      categoryIds?: string[];
      author?: string;
    }>;
    teams: Array<{
      _id: string;
      name: string;
      memberCount?: number;
    }>;
    members: Array<{
      userId?: string;
      name?: string;
      email?: string;
      role?: string;
      teamId: string;
      teamName: string;
    }>;
  };
}

interface GlobalSearchAssetsEngagesResult {
  globalSearch: {
    assets: Array<{
      _id: string;
      name?: string;
      originalFileName?: string;
      type?: string;
      fileType?: string;
      appType?: string;
      thumbnail?: string;
    }>;
    engages: Array<{
      _id: string;
      name?: string;
      originalFileName?: string;
      type?: string;
      fileType?: string;
      appType?: string;
      thumbnail?: string;
    }>;
  };
}

const ENTITY_ROUTES: Record<string, string> = {
  screens: '/app/screenManagement',
  assets: '/app/assetManagement',
  playlists: '/app/playlistManagement',
  schedules: '/app/scheduleManagement',
  operationalSchedules: '/app/s/operational-schedule',
  engages: '/app/engageManagement',
};

// Maps search entity types to usePermissions entity names for canEdit() checks
const ENTITY_PERMISSION_MAP: Record<string, 'screen' | 'asset' | 'playlist' | 'schedule'> = {
  screens: 'screen',
  assets: 'asset',
  playlists: 'playlist',
  schedules: 'schedule',
  engages: 'asset', // Engage uses asset permissions
};

function getCurrentEntityType(pathname: string): string | null {
  if (pathname.startsWith('/app/screenManagement')) return 'screens';
  if (pathname.startsWith('/app/assetManagement')) return 'assets';
  if (pathname.startsWith('/app/playlistManagement')) return 'playlists';
  if (pathname.startsWith('/app/scheduleManagement')) return 'schedules';
  if (pathname.startsWith('/app/engageManagement')) return 'engages';
  return null;
}

const PAGE_SEARCH_STORES: Record<
  string,
  { getState: () => { setSearchText: (t: string) => void } }
> = {
  screens: useScreenFilterStore,
  assets: useAssetFilterStore,
  playlists: usePlaylistFilterStore,
  schedules: useScheduleSidebarStore,
  engages: useEngageFilterStore,
};

const GROUP_ORDER = [
  'screens',
  'assets',
  'playlists',
  'schedules',
  'folders',
  'feeds',
  'engages',
  'operationalSchedules',
  'teams',
  'members',
] as const;

const FOLDER_TYPE_LABELS: Record<string, string> = {
  DEVICE: 'Screen',
  ASSET: 'Asset',
  PLAYLIST: 'Playlist',
  SCHEDULE: 'Schedule',
  ENGAGE: 'Engage',
};

/** Non-matching sentinel so cmdk doesn't auto-highlight the first item */
const NO_SELECTION = '__none__';

interface GlobalSearchBarProps {
  compact?: boolean;
}

export function GlobalSearchBar({ compact }: GlobalSearchBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTeamId } = useTeamStore();
  const { isRootSuperAdmin, isTeamAdmin, canEdit } = usePermissions();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedValue, setSelectedValue] = useState(NO_SELECTION);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Track whether user explicitly arrow-navigated (vs cmdk auto-selecting first item)
  const hasArrowNavigated = useRef(false);
  // Block cmdk auto-highlight until user interacts (arrow keys or mouse hover)
  const hasInteracted = useRef(false);

  const [executeSearch, { data, loading }] = useLazyQuery<GlobalSearchResult>(GLOBAL_SEARCH, {
    fetchPolicy: 'network-only',
  });
  const [executeAssetsEngagesSearch, { data: assetsEngagesData, loading: assetsEngagesLoading }] =
    useLazyQuery<GlobalSearchAssetsEngagesResult>(GLOBAL_SEARCH_ASSETS_ENGAGES, {
      fetchPolicy: 'network-only',
    });

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when panel opens, clear query when it closes
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery('');
    }
  }, [open]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    // Delay listener so the trigger click doesn't immediately close
    const id = setTimeout(() => document.addEventListener('mousedown', handleClick), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Debounced search
  const handleValueChange = useCallback(
    (value: string) => {
      setQuery(value);
      setSelectedValue(NO_SELECTION);
      hasArrowNavigated.current = false;
      hasInteracted.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      // Atlas Search autocomplete requires min 2 chars
      if (value.trim().length < 2) return;
      debounceRef.current = setTimeout(() => {
        const trimmed = value.trim();
        // Fire both queries in parallel. The fast one lands ~150-250ms, the
        // slow one ~700-1500ms. UI renders each section as its data lands.
        executeSearch({
          variables: { teamId: activeTeamId, query: trimmed, limit: 5 },
        });
        executeAssetsEngagesSearch({
          variables: { teamId: activeTeamId, query: trimmed, limit: 5 },
        });
      }, 300);
    },
    [activeTeamId, executeSearch, executeAssetsEngagesSearch],
  );

  // Navigate to entity page with deep-link.
  // Pre-set Zustand stores for playlists/schedules so their URL sync effects
  // don't race against the deep-link and overwrite it.
  const handleSelect = useCallback(
    (entityType: string, id: string, item: Record<string, unknown>) => {
      setOpen(false);
      const route = ENTITY_ROUTES[entityType];
      if (!route) return;

      // Build folder navigation URL from parentGroupInfo (shared by both permission checks)
      const parentGroup = item.parentGroupInfo as
        | {
            name?: string;
            path?: string | null;
            teamId?: string;
            isFolderSecurityEdit?: boolean;
          }
        | undefined;
      // Check permissions (Level 1: role + Level 2: folder security)
      const permEntity = ENTITY_PERMISSION_MAP[entityType];
      const hasRolePermission = !permEntity || canEdit(permEntity);
      let hasFolderPermission = true;
      if (parentGroup?.isFolderSecurityEdit === false) {
        const isSameTeam = !parentGroup.teamId || parentGroup.teamId === activeTeamId;
        hasFolderPermission = isRootSuperAdmin || (isTeamAdmin && isSameTeam);
      }

      if (entityType === 'playlists') {
        usePlaylistSelectionStore.getState().setActivePlaylist(id);
      } else if (entityType === 'schedules') {
        useScheduleSelectionStore.getState().selectSchedule(id);
      }

      // Build URL: always include folder path, add id only when user has permission
      const params = new URLSearchParams();
      if (parentGroup?.name) {
        const parentPath = parentGroup.path && parentGroup.path !== '/' ? parentGroup.path : '';
        params.set('path', `${parentPath}/${parentGroup.name}`);
        if (parentGroup.teamId && parentGroup.teamId !== activeTeamId) {
          params.set('teamId', parentGroup.teamId);
        }
      }

      if (!hasRolePermission || !hasFolderPermission) {
        // No permission — navigate to folder only + toast
        toast.error(t('You do not have permission to edit this asset'));
      } else {
        // Has permission — include id to trigger deep-link auto-open edit dialog
        params.set('id', id);
      }

      const qs = params.toString();
      navigate(qs ? `${route}?${qs}` : route);
    },
    [navigate, t, activeTeamId, isRootSuperAdmin, isTeamAdmin, canEdit],
  );

  const handleFeedSelect = useCallback(
    (id: string) => {
      setOpen(false);
      navigate(`/app/assetManagement?feed_id=${id}`);
    },
    [navigate],
  );

  // Deep-link into Account Members filtered to a specific team. Both team-row
  // and member-row clicks scope the destination page to that one team so the
  // "one row per (member, team) pairing" split carries through visually — row
  // 1 (Default) and row 2 (Team A) each land on their own team section.
  const handleTeamSelect = useCallback(
    (team: { _id: string; name: string }) => {
      setOpen(false);
      const params = new URLSearchParams({ teamId: team._id, teamName: team.name });
      navigate(`/app/s/account-members?${params.toString()}`);
    },
    [navigate],
  );

  const handleMemberSelect = useCallback(
    (member: { teamId: string; teamName: string }) => {
      setOpen(false);
      const params = new URLSearchParams({ teamId: member.teamId, teamName: member.teamName });
      navigate(`/app/s/account-members?${params.toString()}`);
    },
    [navigate],
  );

  const handleFolderSelect = useCallback(
    (folder: { _id: string; name: string; type?: string; path?: string; teamId?: string }) => {
      setOpen(false);
      const folderType = folder.type?.toUpperCase();
      // folder.type from API matches GROUP_TYPES values: device, asset, playlist, schedule, engage
      const routeMap: Record<string, string> = {
        DEVICE: '/app/screenManagement',
        ASSET: '/app/assetManagement',
        PLAYLIST: '/app/playlistManagement',
        SCHEDULE: '/app/scheduleManagement',
        ENGAGE: '/app/engageManagement',
      };
      const route = routeMap[folderType ?? ''] ?? '/app/assetManagement';
      // Root-level folders have path=null or path='/'; nested have path='/Parent'
      const parentPath = folder.path && folder.path !== '/' ? folder.path : '';
      const folderPath = `${parentPath}/${folder.name}`;
      const params = new URLSearchParams({ path: folderPath });
      // Only include teamId for cross-team shared folders
      if (folder.teamId && folder.teamId !== activeTeamId) {
        params.set('teamId', folder.teamId);
      }
      navigate(`${route}?${params.toString()}`);
    },
    [navigate, activeTeamId],
  );

  // Order groups with current page entity first
  const currentEntity = getCurrentEntityType(location.pathname);
  const orderedGroups = currentEntity
    ? [currentEntity, ...GROUP_ORDER.filter((g) => g !== currentEntity)]
    : [...GROUP_ORDER];

  const results = data?.globalSearch;
  const assetsEngagesResults = assetsEngagesData?.globalSearch;
  const hasResults =
    (!!results &&
      (results.screens.length > 0 ||
        results.playlists.length > 0 ||
        results.schedules.length > 0 ||
        results.folders.length > 0 ||
        results.feeds.length > 0 ||
        (results.teams?.length ?? 0) > 0 ||
        (results.members?.length ?? 0) > 0)) ||
    (!!assetsEngagesResults &&
      ((assetsEngagesResults.assets?.length ?? 0) > 0 ||
        (assetsEngagesResults.engages?.length ?? 0) > 0));

  const groupLabels: Record<string, string> = {
    screens: t('Screens'),
    assets: t('Files/Assets'),
    playlists: t('Playlists'),
    schedules: t('Schedules'),
    operationalSchedules: t('Operational Schedules'),
    folders: t('Folders'),
    feeds: t('Feeds'),
    engages: t('Engage'),
    teams: t('Teams'),
    members: t('Members'),
  };

  const groupIcons: Record<string, React.ReactNode> = {
    screens: <Monitor className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    assets: <Image className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    playlists: <ListVideo className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    schedules: <Calendar className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    operationalSchedules: <CalendarClock className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    folders: <Folder className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    feeds: <Rss className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    engages: <Smartphone className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    teams: <Users2 className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    members: <User className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
  };

  function getItemName(entityType: string, item: Record<string, unknown>): string {
    if (entityType === 'screens') {
      return (item.deviceName as string) || (item.name as string) || (item._id as string);
    }
    if (entityType === 'assets' || entityType === 'engages') {
      return (
        (item.originalFileName as string) ||
        (item.appType as string) ||
        (item.name as string) ||
        t('Untitled')
      );
    }
    if (entityType === 'members') {
      return (item.name as string) || (item.email as string) || t('Unknown');
    }
    // Synthetic Default Team row — backend returns hardcoded English 'Default Team';
    // localize for display via i18n so a Spanish user sees "Equipo predeterminado".
    if (entityType === 'teams' && item._id === ROOT_TEAM_ID) {
      return t('Default Team');
    }
    return (item.name as string) || (item._id as string);
  }

  function getFolderSubtitle(item: Record<string, unknown>): string | null {
    const type = item.type as string | undefined;
    if (!type) return null;
    const label = FOLDER_TYPE_LABELS[type.toUpperCase()];
    return label ? `${t(label)} ${t('Folder')}` : null;
  }

  function getGroupItems(entityType: string): Array<Record<string, unknown>> | null {
    // Assets and engages come from the parallel slow query. Return null while
    // the slow query is in flight — otherwise Apollo's `fetchPolicy: 'network-only'`
    // keeps the PREVIOUS search's data populated until the new response lands,
    // causing stale assets/engages rows (with wrong names and wrong
    // parentGroupInfo) to render alongside fresh screens/playlists rows. The
    // renderGroup() spinner path handles the visible loading state — this just
    // makes sure no stale row can slip through if the spinner path is refactored.
    if (entityType === 'assets') {
      if (assetsEngagesLoading) return null;
      const items = assetsEngagesResults?.assets as Array<Record<string, unknown>> | undefined;
      return items && items.length > 0 ? items : null;
    }
    if (entityType === 'engages') {
      if (assetsEngagesLoading) return null;
      const items = assetsEngagesResults?.engages as Array<Record<string, unknown>> | undefined;
      return items && items.length > 0 ? items : null;
    }
    if (!results) return null;
    // Operational schedules are a virtual group — split from the schedules data by type
    if (entityType === 'operationalSchedules') {
      const schedules = results.schedules as Array<Record<string, unknown>>;
      if (!schedules) return null;
      const ops = schedules.filter((s) => s.type === 'Ops');
      return ops.length > 0 ? ops : null;
    }
    if (entityType === 'schedules') {
      const schedules = results.schedules as Array<Record<string, unknown>>;
      if (!schedules) return null;
      const regular = schedules.filter((s) => !s.type);
      return regular.length > 0 ? regular : null;
    }
    const items = results[entityType as keyof typeof results] as Array<Record<string, unknown>>;
    return items?.length > 0 ? items : null;
  }

  function getRowKey(entityType: string, item: Record<string, unknown>, index: number): string {
    if (entityType === 'members') {
      // Members repeat per team pairing, so _id alone isn't unique.
      const uid = (item.userId as string) || (item.email as string) || String(index);
      return `${item.teamId as string}-${uid}`;
    }
    return item._id as string;
  }

  function renderGroup(entityType: string) {
    // Progressive rendering: while the slow (assets+engages) query is
    // in flight, HIDE those sections entirely — no header, no rows. A
    // single "Loading..." footer at the bottom of the results (rendered
    // outside this function, see below the orderedGroups.map) covers
    // the slow bucket. Less visual noise than one spinner per hidden
    // section, and avoids the stale-data risk of Apollo's
    // `fetchPolicy: 'network-only'` keeping the previous search's rows
    // in `assetsEngagesResults` until the new response lands.
    if ((entityType === 'assets' || entityType === 'engages') && assetsEngagesLoading) {
      return null;
    }

    const items = getGroupItems(entityType);
    if (!items) return null;

    return (
      <CommandGroup key={entityType} heading={groupLabels[entityType]}>
        {items.map((item, index) => (
          <CommandItem
            key={getRowKey(entityType, item, index)}
            value={`${entityType}-${getRowKey(entityType, item, index)}`}
            data-testid={`global-search-row-${entityType}-${getRowKey(entityType, item, index)}`}
            onSelect={() => {
              if (entityType === 'folders') {
                handleFolderSelect(
                  item as {
                    _id: string;
                    name: string;
                    type?: string;
                    path?: string;
                    teamId?: string;
                  },
                );
              } else if (entityType === 'feeds') {
                handleFeedSelect(item._id as string);
              } else if (entityType === 'operationalSchedules') {
                setOpen(false);
                const id = item._id as string;
                navigate(
                  id ? `/app/s/operational-schedule?id=${id}` : '/app/s/operational-schedule',
                );
              } else if (entityType === 'teams') {
                handleTeamSelect(item as { _id: string; name: string });
              } else if (entityType === 'members') {
                handleMemberSelect(item as { teamId: string; teamName: string });
              } else {
                handleSelect(entityType, item._id as string, item);
              }
            }}
          >
            {groupIcons[entityType]}
            <div className="flex min-w-0 flex-col">
              <span className="truncate">{getItemName(entityType, item)}</span>
              {entityType === 'folders' && getFolderSubtitle(item) && (
                <span className="truncate text-xs text-muted-foreground">
                  {getFolderSubtitle(item)}
                </span>
              )}
              {entityType === 'feeds' && (item.author as string) && (
                <span className="truncate text-xs text-muted-foreground">
                  {item.author as string}
                </span>
              )}
              {entityType === 'teams' && typeof item.memberCount === 'number' && (
                <span className="truncate text-xs text-muted-foreground">
                  {`${item.memberCount as number} ${t('Members')}`}
                </span>
              )}
              {entityType === 'members' && (
                <span className="truncate text-xs text-muted-foreground">
                  {[
                    item.email as string,
                    item.teamId === ROOT_TEAM_ID ? t('Default Team') : (item.teamName as string),
                  ]
                    .filter(Boolean)
                    .join(' • ')}
                </span>
              )}
            </div>
          </CommandItem>
        ))}
      </CommandGroup>
    );
  }

  return (
    <>
      {/* Trigger in nav bar */}
      {compact ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-sidebar-active/60"
                aria-label={t('Search')}
                data-testid="global-search-trigger"
                onClick={() => setOpen(true)}
              >
                <Search className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="flex items-center gap-1">
              {t('Search')}
              <kbd className="inline-flex items-center gap-0.5 rounded border bg-muted px-1 font-mono text-[10px] font-medium text-muted-foreground">
                <span className="text-xs">&#8984;</span>K
              </kbd>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-64 justify-start gap-2 border border-border bg-card text-muted-foreground hover:bg-card hover:text-foreground hover:shadow-sm"
                data-testid="global-search-trigger"
                onClick={() => setOpen(true)}
              >
                <Search className="h-3.5 w-3.5 shrink-0" />
                <span className="text-sm flex-1 text-left">{t('Search')}...</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="flex items-center gap-1">
              {t('Search')}
              <kbd className="inline-flex items-center gap-0.5 rounded border bg-muted px-1 font-mono text-[10px] font-medium text-muted-foreground">
                <span className="text-xs">&#8984;</span>K
              </kbd>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Floating search panel — centered in the nav, no backdrop */}
      {open && (
        <div
          ref={panelRef}
          data-testid="global-search-panel"
          className="fixed left-1/2 top-3 z-[60] w-[600px] -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-card shadow-[0_8px_30px_rgba(0,0,0,0.16)]"
        >
          <Command
            shouldFilter={false}
            value={selectedValue}
            onValueChange={(v) => {
              if (hasInteracted.current) setSelectedValue(v);
            }}
          >
            <div className="flex items-center border-b px-3 py-0.5">
              <Search className="mr-2.5 h-4.5 w-4.5 shrink-0 text-muted-foreground" />
              <input
                ref={inputRef}
                data-testid="global-search-input"
                className="flex h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                placeholder={t('Search screens, assets, playlists, schedules...')}
                value={query}
                onChange={(e) => handleValueChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    hasInteracted.current = true;
                    hasArrowNavigated.current = true;
                  }
                  if (isEnterKey(e) && !hasArrowNavigated.current) {
                    // User typed and hit Enter without arrow-navigating — search on current page
                    e.preventDefault();
                    const entity = getCurrentEntityType(location.pathname);
                    const trimmed = query.trim();
                    if (!entity || !trimmed) return;
                    PAGE_SEARCH_STORES[entity].getState().setSearchText(trimmed);
                    setOpen(false);
                  }
                  // If user arrow-navigated, let cmdk handle Enter (fires onSelect)
                }}
              />
              {query && (
                <button
                  className="ml-2 rounded-sm opacity-50 hover:opacity-100"
                  onClick={() => {
                    setQuery('');
                    inputRef.current?.focus();
                  }}
                  aria-label={t('Clear search')}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <CommandList
              className="max-h-[360px]"
              onMouseMove={() => {
                hasInteracted.current = true;
              }}
            >
              {/* Reserved empty space while anything is loading and nothing
                  useful has landed. Keeps the panel a consistent height
                  during Phase 1 so it doesn't collapse to zero. The visible
                  "Searching..." text lives only in the bottom footer below
                  — one indicator, not two. */}
              {(loading || assetsEngagesLoading) && !hasResults && query.trim() && (
                <div className="py-6" />
              )}
              {!loading && !assetsEngagesLoading && query.trim().length === 1 && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {t('Type one more character to search...')}
                </div>
              )}
              {/* "No results" only after BOTH queries finished. */}
              {!loading && !assetsEngagesLoading && query.trim().length >= 2 && !hasResults && (
                <CommandEmpty>{t('No results found.')}</CommandEmpty>
              )}
              {/* Results block — rendered once at least one query has landed
                  with content. During Phase 1 the reserved-space block above
                  keeps the panel from collapsing; the always-on footer below
                  provides the visible loading signal. */}
              {query.trim() && hasResults && (
                <>
                  {/* Separator counter is based on ACTUALLY rendered groups,
                      not the original orderedGroups index — otherwise a null
                      earlier group (empty results or assets/engages hiding
                      while the slow bucket loads) would leave a stray leading
                      separator above the first rendered group. */}
                  {orderedGroups
                    .map((entityType) => [entityType, renderGroup(entityType)] as const)
                    .filter(
                      (tuple): tuple is readonly [string, React.JSX.Element] => tuple[1] !== null,
                    )
                    .map(([entityType, group], i) => (
                      <div key={entityType}>
                        {i > 0 && <CommandSeparator />}
                        {group}
                      </div>
                    ))}
                </>
              )}
              {/* Single always-on "Searching..." footer while ANY query is
                  in flight — the only loading indicator in the panel. Fires
                  during Phase 1 (nothing landed yet) and Phase 2 (fast
                  landed, slow still loading). Separator only renders when
                  there are results above to divide from. */}
              {(loading || assetsEngagesLoading) && query.trim().length >= 2 && (
                <>
                  {hasResults && <CommandSeparator />}
                  <div
                    className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground"
                    data-testid="global-search-loading-more"
                  >
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                    <span>{t('Searching...')}</span>
                  </div>
                </>
              )}
            </CommandList>
          </Command>
        </div>
      )}
    </>
  );
}
